// Deps
import React from 'react'
import styled, { ThemeContext } from 'styled-components'
import { ButtonVariants, ThemeVariants, ThemesNames } from '../../constants'
// Comps
import Icon from '../Icon'
import {
    borderRadiusRule,
    getCSSVariable,
    buttonBackgroundColorValue,
    buttonLabelColorValue,
    buttonBorderRule,
    fontSizeRule,
} from '../../constants/cssRules'

/**
 * Returns the icon variant based on the provided parameters.
 *
 * @param {Object} options - The options object.
 * @param {boolean} options.disabled - Indicates if the button is disabled.
 * @param {string} options.variant - The button variant.
 * @returns {string} The icon variant.
 */
const getIconVariant = ({ disabled, variant }) => {
    let iconVariant
    if (disabled) {
        iconVariant = ThemeVariants.DARK
    } else {
        switch (variant) {
            case ButtonVariants.MAIN:
                iconVariant = ThemeVariants.WHITE
                break
            case ButtonVariants.DANGER:
                iconVariant = ThemeVariants.WHITE
                break
            case ButtonVariants.DANGER_SECONDARY:
                iconVariant = ThemeVariants.ERROR
                break
            case ButtonVariants.SECONDARY:
                iconVariant = ThemeVariants.MAIN
                break
            default:
                iconVariant = ThemeVariants.MAIN
                break
        }
    }
    return iconVariant
}

const StyledButton = styled.button`
    ${props =>
        props.themeContext.main &&
        `
        user-select: none;
        width: auto;
        min-width: auto;
        max-width: unset;
        float: none;
        display: inline-block;
        display: inline-flex;
        outline: 0;
        align-items: center;
        justify-content: space-around;
        background-color: ${buttonBackgroundColorValue(props)};
        color: ${buttonLabelColorValue(props)};
        padding: ${getCSSVariable('buttonPadding')};
        ${buttonBorderRule(props)}
        ${borderRadiusRule(props)}
        ${fontSizeRule('15px')}
        font-weight: 600;
        transition: 0.2s all;
        ${
            props.labeled
                ? `
                    & svg {
                        margin: 0 9px 0 0;
                    }`
                : 'padding: 10px;'
        }
        ${
            !props.disabled
                ? `
                    cursor: pointer;
                    &:hover {
                        background: ${buttonBackgroundColorValue({
                            ...props,
                            isHover: true,
                        })} !important;
                        ${
                            props.variant === ButtonVariants.MAIN
                                ? `box-shadow: 0 10px 40px -5px ${props.themeContext.main}90;`
                                : ''
                        }
                    }
                `
                : `
                    cursor: not-allowed;
                    filter: grayscale(1);
                `
        }
        * {
            pointer-events: none;
        }
        `}
`

const Button = ({
    id,
    cardHoverOnly,
    type,
    label,
    variant,
    filled,
    onClick,
    style,
    icon,
    className,
    disabled,
    title,
}) => {
    const themeContext = React.useContext(ThemeContext)

    return type === 'input' ? (
        <input type='submit' value={label} className='rds-btn' />
    ) : (
        <StyledButton
            className={`rds-btn align-center justify-around ${
                cardHoverOnly ? 'rds-btn_card-hover' : ''
            } ${variant || ''} ${className || ''}`}
            themeContext={themeContext}
            id={id}
            variant={variant}
            onClick={!disabled ? onClick : undefined}
            type='button'
            style={style}
            labeled={label}
            disabled={disabled}
            title={title}
        >
            <div
                className='rds-flexbox align-center'
                style={{ width: 'max-content' }}
            >
                {icon ? (
                    <Icon
                        type={icon}
                        variant={getIconVariant({ disabled, variant })}
                        theme={filled ? 'filled' : null}
                        size={15}
                    />
                ) : null}
                <span>{label}</span>
            </div>
        </StyledButton>
    )
}

export default Button
